import React, {useCallback, useEffect, useState} from "react";
import {useRouter} from "next/router";
import {MdPhotoLibrary} from "react-icons/md";
import {FaAngleLeft, FaAngleRight} from "react-icons/fa";
import SharedServiceInstance from "@/services/shared-service";
import ApiService from "@/services/api-service";
import PostMoreBlock from "@/components/shared/post-more-block";
import LoadingCommon from "@/components/shared/loadingCommon";
import PaginationCustom from "@/components/shared/pagination-custom";
import ModalSliderAlbum from "@/components/shared/modal-slider-album";
import Image from "next/image";

const PAGE_SIZE = 12;

export default function CategoryAlbum() {
    const router = useRouter();
    const page = parseInt(router.query.page || "1", 10);

    const [albums, setAlbums] = useState(null);
    const [pageData, setPageData] = useState({currentPage: page, pageSize: PAGE_SIZE, pageCount: 0});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPhotos, setSelectedPhotos] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);

    const [albumIndexes, setAlbumIndexes] = useState({});

    const fetchAlbumData = useCallback(async () => {
        const skip = (page - 1) * PAGE_SIZE;
        const data = await ApiService.getAlbums({top: PAGE_SIZE, skip, isCount: true, sort: "created desc"});

        if (data) {
            const total = parseInt(data?.["@odata.count"] || "0", 10);
            setAlbums(data);
            setPageData({
                currentPage: page,
                pageSize: PAGE_SIZE,
                pageCount: total > 0 ? Math.ceil(total / PAGE_SIZE) : 0
            });
        } else {
            setAlbums({});
        }

    }, [page]);

    useEffect(() => {
        if (router.isReady) fetchAlbumData().then(r => {});
    }, [fetchAlbumData, router.isReady]);

    useEffect(() => {
        document.body.style.overflow = isModalOpen ? "hidden" : "auto";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isModalOpen]);

    const openModal = (photos, index = 0) => {
        setSelectedPhotos(photos);
        setCurrentIndex(index);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPhotos([]);
        setCurrentIndex(0);
    };

    if (!albums || parseInt(albums["@odata.count"] || "0", 10) === 0) {
        return <LoadingCommon itemName="album hình"/>;
    }

    return (
        <>
            <div className="w-full">
                <h2 className="font-bold text-5xl pt-5 text-center sm:text-left px-2 text-primary">
                    Album Hình
                </h2>

                <div className="sm:grid sm:grid-cols-4 my-5 gap-5 xl:gap-10 px-2 sm:px-0">
                    <div className="sm:col-span-3 bg-white p-3 h-fit">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <AlbumList
                                albums={albums.value}
                                albumIndexes={albumIndexes}
                                setAlbumIndexes={setAlbumIndexes}
                                openModal={openModal}
                            />
                        </div>
                    </div>

                    <div className="posts-categories--more hidden md:block">
                        <PostMoreBlock/>
                    </div>
                </div>
            </div>

            {pageData.pageCount > 1 && (
                <div className="flex justify-center pb-5">
                    <PaginationCustom asPath={router.asPath} pageData={pageData}/>
                </div>
            )}

            <div className="posts-categories--more block md:hidden">
                <PostMoreBlock/>
            </div>

            {isModalOpen && (
                <ModalSliderAlbum
                    selectedPhotos={selectedPhotos}
                    currentIndex={currentIndex}
                    setCurrentIndex={setCurrentIndex}
                    closeModal={closeModal}
                />
            )}
        </>
    );
}

const AlbumList = ({albums, albumIndexes, setAlbumIndexes, openModal}) => {
    useEffect(() => {
        albums.forEach((album, index) => {
            const photos = album.photoUrls;
            if (!Array.isArray(photos) || photos.length === 0) return;

            const currentPhotoIndex = albumIndexes?.[index] ?? 0;
            const total = photos.length;

            // Preload 2 images before and after the current index
            for (let offset = -2; offset <= 2; offset++) {
                if (offset === 0 || total === 0) continue;

                const preloadIndex = (currentPhotoIndex + offset + total) % total;
                const img = new window.Image();
                img.src = SharedServiceInstance.getOptimizeImage(photos[preloadIndex], "album");
            }
        });
    }, [albumIndexes, albums]);

    return albums.map((album, index) => {
        const photos = album.photoUrls;
        if (!Array.isArray(photos) || photos.length === 0) return null;

        const currentPhotoIndex = albumIndexes?.[index] ?? 0;

        const handleSwipe = (dir = "left" | "right") => {
            if (photos.length <= 1) return;

            const newIndex =
                dir === "left"
                    ? (currentPhotoIndex + 1) % photos.length
                    : (currentPhotoIndex - 1 + photos.length) % photos.length;

            setAlbumIndexes(prev => ({...prev, [index]: newIndex}));
        };

        return (
            <div key={album.id} className="relative bg-white shadow rounded overflow-hidden">
                <div className="aspect-[16/9] w-full relative overflow-hidden">
                    <Image
                        width={1000}
                        height={1000}
                        key={currentPhotoIndex}
                        src={SharedServiceInstance.getOptimizeImage(photos[currentPhotoIndex], "album")}
                        alt={`${album.name}-${currentPhotoIndex}`}
                        className="w-full h-full object-cover flex-shrink-0 cursor-pointer"
                        onClick={() => openModal(photos, currentPhotoIndex)}
                    />

                    {photos.length > 1 && (
                        <>
                            <button
                                onClick={() => handleSwipe("right")}
                                className="absolute left-0 top-1/2 -translate-y-1/2 text-white text-opacity-80 hover:text-white p-2 text-2xl z-10"
                            >
                                <FaAngleLeft/>
                            </button>
                            <button
                                onClick={() => handleSwipe("left")}
                                className="absolute right-0 top-1/2 -translate-y-1/2 text-white text-opacity-80 hover:text-white p-2 text-2xl z-10"
                            >
                                <FaAngleRight/>
                            </button>
                        </>
                    )}
                </div>

                <div title={album.name}
                     className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent text-white p-3 cursor-default"
                >
                    <h3 className="text-lg font-semibold truncate">{album.name}</h3>
                    <div className="flex items-center text-sm space-x-1">
                        <MdPhotoLibrary className="w-4 h-4 text-white"/>
                        <span>{photos.length} ảnh</span>
                    </div>
                </div>
            </div>
        );
    });
};